import type { Status } from '@cvo/ui/types/designSystem';
import type { ApiError, ApiErrorDetail } from '@cvo/domain';
import {
  type BookingEngineErrorCode,
  getBookingEngineErrorCode,
  isBookingPaymentFailedError,
} from '~/constants/booking';

/** Booking engine codes live beside the other booking API error codes. */
export const BOOKING_ENGINE_ERROR_I18N_PREFIX = 'ui.booking.error';

interface HandleOptions {
  /** Re-runs the failed request; wired to "Try again" (UNAVAILABLE) and "Retry payment" (PAYMENT_REJECTED). */
  retry?: () => unknown;
}

type ActionKind = 'contactSupport' | 'completeDetails' | 'restartSearch' | 'goHome' | 'retry';

type Treatment =
  | { component: 'notification'; status: Status; action: ActionKind; persistent?: boolean }
  | { component: 'modal'; action: ActionKind };

type NotificationTreatment = Extract<Treatment, { component: 'notification' }>;

/** Error code → UI mapping table. */
const TREATMENTS: Record<BookingEngineErrorCode, Treatment> = {
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-NOT-PERMITTED': { component: 'notification', status: 'danger', action: 'contactSupport' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PASSENGER-DATA-MISMATCH': { component: 'notification', status: 'danger', action: 'contactSupport' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PASSENGER-DATA-INCOMPLETE': { component: 'notification', status: 'danger', action: 'completeDetails' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-SESSION-EXPIRED': { component: 'notification', status: 'warning', action: 'restartSearch' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-BOOKING-WINDOW-CLOSED': { component: 'notification', status: 'warning', action: 'goHome' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-FLIGHT-NOT-AVAILABLE': { component: 'notification', status: 'warning', action: 'goHome' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-ROUTE-INVALID': { component: 'notification', status: 'warning', action: 'goHome' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-UNAVAILABLE': { component: 'notification', status: 'warning', action: 'retry', persistent: false },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-REJECTED': { component: 'modal', action: 'contactSupport' },
  'CORE-SUBSCRIPTION-BOOKING-ENGINE-PAYMENT-REJECTED': { component: 'modal', action: 'retry' },
};

/**
 * Only the provider's codes reach analytics. Its free-form message can name the passenger,
 * and PII must not travel in analytics payloads. The full detail stays in the API response
 * for support.
 */
const TRACKED_DETAIL_NAMES = ['providerErrorCode', 'alsoReported'];

function trackedProviderCodes(error: unknown): string[] | undefined {
  const details = (error as ApiError<{ details?: ApiErrorDetail[] }>).data?.details;
  return details?.filter(detail => TRACKED_DETAIL_NAMES.includes(detail.name)).map(detail => detail.reason);
}

/**
 * The primary action of the open BookingEngineErrorModal. Kept out of the shared state so that
 * state holds only the serialisable code, and released as soon as the dialog closes.
 */
let modalPrimaryAction: (() => unknown) | undefined;

/** The single BookingEngineErrorModal mounted in App.vue: its code, and the ways to open and close it. */
export function useBookingEngineErrorModal() {
  const code = useState<BookingEngineErrorCode | null>('bookingEngineErrorModal', () => null);

  function open(nextCode: BookingEngineErrorCode, action?: () => unknown) {
    code.value = nextCode;
    modalPrimaryAction = action;
  }

  function close() {
    code.value = null;
    modalPrimaryAction = undefined;
  }

  /** Closes the dialog, then runs the primary action of the code it showed. */
  function confirm() {
    const action = modalPrimaryAction;
    close();
    action?.();
  }

  return { code: readonly(code), open, confirm, close };
}

/**
 * Turns a CORE-SUBSCRIPTION-BOOKING-ENGINE-* API error into its defined notification or modal.
 * `handle` returns false for any other error so callers keep their own fallback message.
 */
export function useBookingEngineError() {
  const { t } = useI18n();
  const notificationsStore = useNotificationsStore();
  const localePath = useLocalePath();
  const o11y = useO11y();
  const { goHome } = useHome();
  const { goBackToSearch } = useLastSearchQuery();
  const { contactSupportLink } = useContactChannels();
  const modal = useBookingEngineErrorModal();
  const { activeId: activeNotificationId, clear } = useBookingEngineErrorNotification();

  function openContactSupport() {
    const { href, external } = contactSupportLink.value;
    if (external) return navigateTo(href, { external: true, open: { target: '_blank' } });
    // mailto:/tel: hand off to the device; an in-app path (FAQ page) stays in the router.
    return href.startsWith('/') ? navigateTo(href) : navigateTo(href, { external: true });
  }

  function resolveAction(kind: ActionKind, retry?: () => unknown): (() => unknown) | undefined {
    const actions: Record<ActionKind, (() => unknown) | undefined> = {
      contactSupport: openContactSupport,
      completeDetails: () => navigateTo(localePath('/passenger-info')),
      restartSearch: goBackToSearch,
      goHome,
      retry,
    };
    return actions[kind];
  }

  function resolveCode(error: unknown): BookingEngineErrorCode | undefined {
    // The pre-existing payment failure shares the "Payment unsuccessful" modal: one failure, one pattern.
    if (isBookingPaymentFailedError(error)) return 'CORE-SUBSCRIPTION-BOOKING-ENGINE-PAYMENT-REJECTED';
    return getBookingEngineErrorCode(error);
  }

  function showNotification(treatment: NotificationTreatment, action: (() => unknown) | undefined, keyPrefix: string) {
    clear();

    const notificationId = notificationsStore.add({
      status: treatment.status,
      message: t(`${keyPrefix}.body`),
      timeout: 0,
      persistent: treatment.persistent ?? true,
      actionPrimary: action ? t(`${keyPrefix}.action`) : undefined,
      onPrimaryClick: action
        ? () => {
            notificationsStore.remove(notificationId);
            activeNotificationId.value = null;
            action();
          }
        : undefined,
      onClose: () => {
        activeNotificationId.value = null;
      },
    });

    activeNotificationId.value = notificationId;
  }

  function handle(error: unknown, options: HandleOptions = {}): boolean {
    const code = resolveCode(error);
    if (!code) return false;

    o11y?.trackAction('booking_engine_error', { code, providerCodes: trackedProviderCodes(error) });

    const treatment = TREATMENTS[code];
    const action = resolveAction(treatment.action, options.retry);
    const keyPrefix = `${BOOKING_ENGINE_ERROR_I18N_PREFIX}.${code}`;

    if (treatment.component === 'modal') {
      modal.open(code, action);
      return true;
    }

    showNotification(treatment, action, keyPrefix);

    return true;
  }

  return { handle, clear };
}
